import { createServerFn } from "@tanstack/react-start";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";
import { z } from "zod";

// -------- Schemas --------
const StatusEnum = z.enum([
  "draft","sent","viewed","approved","declined","changes_requested","expired","void",
]);

const LineItemSchema = z.object({
  id: z.string().uuid().optional(),
  title: z.string().min(1).max(200),
  description: z.string().max(2000).nullable().optional(),
  area_or_quantity: z.number().nullable().optional(),
  unit: z.string().max(40).nullable().optional(),
  unit_price: z.number().nullable().optional(),
  total: z.number().default(0),
  sort_order: z.number().int().default(0),
});

const ColorSchema = z.object({
  id: z.string().uuid().optional(),
  surface: z.string().min(1).max(80),
  color_name: z.string().max(120).nullable().optional(),
  color_number: z.string().max(40).nullable().optional(),
  hex: z.string().max(20).nullable().optional(),
  lrv: z.number().nullable().optional(),
  product_line: z.string().max(120).nullable().optional(),
  sheen: z.string().max(60).nullable().optional(),
  coats: z.number().int().min(1).max(5).default(2),
  sort_order: z.number().int().default(0),
});

const ProposalPatchSchema = z.object({
  customer_name: z.string().min(1).max(200).optional(),
  customer_email: z.string().email().max(200).nullable().optional(),
  customer_phone: z.string().max(60).nullable().optional(),
  property_address: z.string().max(400).nullable().optional(),
  project_type: z.string().max(80).nullable().optional(),
  estimated_timeline: z.string().max(200).nullable().optional(),
  start_date: z.string().nullable().optional(),
  status: StatusEnum.optional(),
  subtotal: z.number().optional(),
  discount: z.number().optional(),
  total: z.number().optional(),
  deposit_required: z.boolean().optional(),
  deposit_amount: z.number().optional(),
  deposit_percentage: z.number().nullable().optional(),
  scope: z.array(z.string().max(500)).optional(),
  warranty: z.string().max(8000).nullable().optional(),
  terms: z.string().max(20000).nullable().optional(),
  notes: z.string().max(4000).nullable().optional(),
  cover_image_url: z.string().url().nullable().optional(),
  design_image_url: z.string().url().nullable().optional(),
  studio_render_url: z.string().url().nullable().optional(),
  expires_at: z.string().nullable().optional(),
});

async function assertAdmin(ctx: { userId: string; supabase: any }) {
  const { data } = await ctx.supabase.rpc("has_role", { _user_id: ctx.userId, _role: "admin" });
  const { data: sup } = await ctx.supabase.rpc("has_role", { _user_id: ctx.userId, _role: "super_admin" });
  if (!data && !sup) throw new Error("Forbidden");
}

function defaultScope(): string[] {
  return [
    "Protect floors, furniture, and fixtures with drop cloths and plastic.",
    "Prepare all surfaces (clean, dust, de-gloss as needed).",
    "Patch minor nail holes and small drywall imperfections.",
    "Sand surfaces as needed for proper adhesion.",
    "Caulk gaps along trim, baseboards, and seams where appropriate.",
    "Apply primer where required (new drywall, bare wood, stain blocking).",
    "Apply selected paint products per manufacturer specifications.",
    "Daily and final job-site cleanup.",
    "Final walkthrough with the homeowner.",
  ];
}

// Resolve the customer-facing origin for links in emails. Never trust the
// admin's browser origin (which can be a lovableproject.com preview URL).
function resolvePublicOrigin(settings?: { company_website?: string | null } | null): string {
  const raw =
    process.env.PUBLIC_SITE_URL ||
    settings?.company_website ||
    "https://michaelspaintingwi.com";
  const trimmed = raw.trim().replace(/\/$/, "");
  return /^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`;
}

// Upsert the proposal's customer into the CRM so it appears in Contacts / Pipeline.
// Fail-soft: never block proposal creation/sending on CRM sync errors.
async function syncProposalToCrm(
  supabaseAdmin: any,
  prop: {
    id: string;
    proposal_number?: string | null;
    customer_name?: string | null;
    customer_email?: string | null;
    customer_phone?: string | null;
    property_address?: string | null;
    total?: number | null;
  },
  opts: { advanceToProposal?: boolean } = {},
): Promise<void> {
  try {
    const emailLc = (prop.customer_email || "").trim().toLowerCase();
    const nowIso = new Date().toISOString();
    let contactId: string | undefined;
    let existing: { id: string; pipeline_stage: string | null } | null = null;

    if (emailLc) {
      const { data } = await supabaseAdmin
        .from("crm_contacts")
        .select("id, pipeline_stage")
        .ilike("email", emailLc)
        .maybeSingle();
      existing = data ?? null;
    }

    const advance = opts.advanceToProposal === true;
    const upstreamStages = new Set([null, undefined, "new", "contacted", "estimate"]);

    if (existing) {
      contactId = existing.id;
      const shouldAdvance = advance && upstreamStages.has(existing.pipeline_stage as any);
      await supabaseAdmin
        .from("crm_contacts")
        .update({
          name: prop.customer_name ?? undefined,
          phone: prop.customer_phone ?? undefined,
          address: prop.property_address ?? undefined,
          last_activity_at: nowIso,
          ...(shouldAdvance ? { pipeline_stage: "proposal" } : {}),
        })
        .eq("id", existing.id);
    } else {
      const { data: created } = await supabaseAdmin
        .from("crm_contacts")
        .insert({
          email: emailLc || null,
          name: prop.customer_name ?? "Customer",
          phone: prop.customer_phone ?? null,
          address: prop.property_address ?? null,
          source: "proposal",
          pipeline_stage: advance ? "proposal" : "new",
          last_activity_at: nowIso,
        })
        .select("id")
        .single();
      contactId = created?.id;
    }

    if (contactId) {
      await supabaseAdmin.from("crm_communications").insert({
        contact_id: contactId,
        kind: "system",
        direction: "outbound",
        subject: advance
          ? `Proposal ${prop.proposal_number ?? ""} sent`.trim()
          : `Proposal ${prop.proposal_number ?? ""} created`.trim(),
        body: prop.total != null ? `Total: $${Number(prop.total).toLocaleString()}` : null,
        metadata: { proposal_id: prop.id, proposal_number: prop.proposal_number } as unknown as never,
      });
    }
  } catch (e) {
    console.error("[proposals] CRM sync failed", e);
  }
}

// -------- Create proposal from estimate --------
export const createProposalFromEstimate = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: unknown) => z.object({ estimate_id: z.string().uuid() }).parse(d))
  .handler(async ({ context, data }) => {
    await assertAdmin(context);
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");

    const { data: est, error: e1 } = await supabaseAdmin
      .from("estimate_requests").select("*").eq("id", data.estimate_id).maybeSingle();
    if (e1 || !est) throw new Error(e1?.message || "Estimate not found");

    const { data: settings } = await supabaseAdmin
      .from("admin_settings").select("*").eq("id", 1).maybeSingle();

    const expDays = settings?.default_proposal_expiration_days ?? 30;
    const depositPct = settings?.default_deposit_percentage ?? 25;
    const sheenMap: Record<string, string> =
      (settings?.default_sheen_by_surface as Record<string, string> | null) || {};
    const paintLines = settings?.default_paint_lines as string[] | null;
    const defaultLine: string = (Array.isArray(paintLines) && paintLines[0]) || "Sherwin-Williams Cashmere";

    const midpoint = Math.round(((est.price_low ?? 0) + (est.price_high ?? 0)) / 2);
    const subtotal = midpoint;
    const total = subtotal;
    const depositAmount = Math.round((total * depositPct) / 100);

    const { data: prop, error: e2 } = await supabaseAdmin
      .from("proposals")
      .insert({
        estimate_request_id: est.id,
        customer_name: est.name ?? "Customer",
        customer_email: est.email,
        customer_phone: est.phone,
        property_address: [est.address, est.city].filter(Boolean).join(", ") || null,
        project_type: est.project_type,
        estimated_timeline: est.estimated_duration_days ? `${Math.ceil(est.estimated_duration_days)} day(s)` : est.timeline,
        subtotal, total, discount: 0,
        deposit_required: depositAmount > 0,
        deposit_amount: depositAmount,
        deposit_percentage: depositPct,
        scope: defaultScope(),
        warranty: settings?.warranty_text || "All workmanship is warranted for 2 years from the date of completion. Paint product warranties are honored per the manufacturer.",
        terms: settings?.default_terms || "Payment due upon completion. Color changes after work begins may incur additional charges. Customer is responsible for moving valuables and small items.",
        expires_at: new Date(Date.now() + expDays * 86400_000).toISOString(),
        design_image_url: est.design_image_url,
        created_by: context.userId,
      })
      .select("*").single();
    if (e2) throw new Error(e2.message);

    // Line items — start with one labor + one materials line at midpoint split
    const labor = Math.round(total * 0.65);
    const materials = total - labor;
    await supabaseAdmin.from("proposal_line_items").insert([
      { proposal_id: prop.id, title: "Labor", description: "Surface preparation and paint application.", total: labor, sort_order: 0 },
      { proposal_id: prop.id, title: "Paint & Materials", description: defaultLine, total: materials, sort_order: 1 },
    ]);

    // Colors from estimate selected_colors
    const selected: any[] = Array.isArray(est.selected_colors) ? est.selected_colors : [];
    if (selected.length) {
      const rows = selected.slice(0, 20).map((c, i) => {
        const surface = (c.surface || c.element || (i === 0 ? "Walls" : "Accent")).toString();
        const key = surface.toLowerCase();
        const sheen = sheenMap[key] || sheenMap.walls || "Eggshell";
        return {
          proposal_id: prop.id,
          surface,
          color_name: c.name || c.label || null,
          color_number: c.number || c.code || null,
          hex: c.hex || c.color || null,
          lrv: typeof c.lrv === "number" ? c.lrv : null,
          product_line: defaultLine,
          sheen,
          coats: 2,
          sort_order: i,
        };
      });
      await supabaseAdmin.from("proposal_colors").insert(rows);
    }

    await supabaseAdmin.from("proposal_activity").insert({
      proposal_id: prop.id, activity_type: "created", actor_kind: "admin", actor_id: context.userId,
      note: `Generated from estimate ${est.id}`,
    });

    await syncProposalToCrm(supabaseAdmin, prop);

    return { id: prop.id, proposal_number: prop.proposal_number, public_token: prop.public_token };
  });

// -------- Create blank proposal (no estimate) --------
export const createBlankProposal = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: unknown) => z.object({
    customer_name: z.string().min(1).max(200),
    customer_email: z.string().email().max(200).nullable().optional(),
    customer_phone: z.string().max(60).nullable().optional(),
    property_address: z.string().max(400).nullable().optional(),
    project_type: z.string().max(80).nullable().optional(),
  }).parse(d))
  .handler(async ({ context, data }) => {
    await assertAdmin(context);
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { data: settings } = await supabaseAdmin
      .from("admin_settings").select("*").eq("id", 1).maybeSingle();
    const expDays = settings?.default_proposal_expiration_days ?? 30;
    const depositPct = settings?.default_deposit_percentage ?? 25;

    const { data: prop, error } = await supabaseAdmin
      .from("proposals")
      .insert({
        customer_name: data.customer_name,
        customer_email: data.customer_email ?? null,
        customer_phone: data.customer_phone ?? null,
        property_address: data.property_address ?? null,
        project_type: data.project_type ?? null,
        subtotal: 0, total: 0, discount: 0,
        deposit_required: false,
        deposit_amount: 0,
        deposit_percentage: depositPct,
        scope: defaultScope(),
        warranty: settings?.warranty_text || "All workmanship is warranted for 2 years from the date of completion. Paint product warranties are honored per the manufacturer.",
        terms: settings?.default_terms || "Payment due upon completion. Color changes after work begins may incur additional charges. Customer is responsible for moving valuables and small items.",
        expires_at: new Date(Date.now() + expDays * 86400_000).toISOString(),
        created_by: context.userId,
      })
      .select("*").single();
    if (error) throw new Error(error.message);

    await supabaseAdmin.from("proposal_line_items").insert([
      { proposal_id: prop.id, title: "Labor", description: "Surface preparation and paint application.", total: 0, sort_order: 0 },
      { proposal_id: prop.id, title: "Paint & Materials", description: "Premium paint and job materials.", total: 0, sort_order: 1 },
    ]);

    await supabaseAdmin.from("proposal_activity").insert({
      proposal_id: prop.id, activity_type: "created", actor_kind: "admin", actor_id: context.userId,
      note: "Created manually (no AI estimate)",
    });

    await syncProposalToCrm(supabaseAdmin, prop);

    return { id: prop.id, proposal_number: prop.proposal_number, public_token: prop.public_token };
  });

// -------- Create proposal from voice dictation --------
const DictationLineSchema = z.object({
  title: z.string().max(200).nullish().transform((v) => (v ?? "").toString().slice(0, 200)),
  description: z.string().max(2000).nullish().transform((v) => (v == null ? null : String(v).slice(0, 2000))),
  area_or_quantity: z.union([z.number(), z.string(), z.null()]).nullish().transform((v) => {
    if (v == null || v === "") return null;
    const n = Number(v); return Number.isFinite(n) ? n : null;
  }),
  unit: z.string().max(40).nullish().transform((v) => (v == null ? null : String(v).slice(0, 40))),
  unit_price: z.union([z.number(), z.string(), z.null()]).nullish().transform((v) => {
    if (v == null || v === "") return null;
    const n = Number(v); return Number.isFinite(n) ? n : null;
  }),
  total: z.union([z.number(), z.string(), z.null()]).nullish().transform((v) => {
    if (v == null || v === "") return 0;
    const n = Number(v); return Number.isFinite(n) ? n : 0;
  }),
}).passthrough();

export const createProposalFromDictation = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: unknown) => {
    const str = (max: number) =>
      z.union([z.string(), z.number(), z.null()]).nullish().transform((v) =>
        v == null || v === "" ? null : String(v).slice(0, max),
      );
    return z
      .object({
        customer_name: z.union([z.string(), z.null()]).nullish().transform((v) => {
          const s = (v ?? "").toString().trim().slice(0, 200);
          return s || "Customer";
        }),
        customer_email: str(200),
        customer_phone: str(60),
        property_address: str(400),
        project_type: str(80),
        estimated_timeline: str(200),
        scope: z
          .union([z.array(z.any()), z.string(), z.null()])
          .nullish()
          .transform((v) => {
            const arr = Array.isArray(v)
              ? v
              : typeof v === "string"
                ? v.split(/\r?\n|(?:^|\s)[•\-\*]\s+|;\s*/g)
                : [];
            return arr
              .map((s) => (s == null ? "" : String(s).trim()))
              .filter((s) => s.length > 0)
              .slice(0, 50);
          }),
        notes: str(4000),
        line_items: z
          .union([z.array(DictationLineSchema), z.null()])
          .nullish()
          .transform((v) => (Array.isArray(v) ? v.slice(0, 80) : [])),
      })
      .passthrough()
      .parse(d ?? {});
  })
  .handler(async ({ context, data }) => {
    await assertAdmin(context);
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { data: settings } = await supabaseAdmin.from("admin_settings").select("*").eq("id", 1).maybeSingle();
    const expDays = settings?.default_proposal_expiration_days ?? 30;
    const depositPct = settings?.default_deposit_percentage ?? 25;

    // Compute totals from the extracted line items.
    const items = data.line_items.map((it, i) => {
      const derived = Number(it.total) > 0
        ? Number(it.total)
        : Number(it.area_or_quantity ?? 1) * Number(it.unit_price ?? 0);
      return { ...it, total: Math.round(derived * 100) / 100, sort_order: i };
    });
    const subtotal = items.reduce((s, it) => s + it.total, 0);
    const deposit = Math.round(subtotal * (Number(depositPct) / 100) * 100) / 100;

    const scope = data.scope.length ? data.scope : defaultScope();
    const emailValue = data.customer_email && /.+@.+\..+/.test(data.customer_email) ? data.customer_email : null;

    const { data: prop, error } = await supabaseAdmin
      .from("proposals")
      .insert({
        customer_name: data.customer_name,
        customer_email: emailValue,
        customer_phone: data.customer_phone ?? null,
        property_address: data.property_address ?? null,
        project_type: data.project_type ?? null,
        estimated_timeline: data.estimated_timeline ?? null,
        subtotal, total: subtotal, discount: 0,
        deposit_required: subtotal > 0,
        deposit_amount: deposit,
        deposit_percentage: depositPct,
        scope,
        notes: data.notes ?? null,
        warranty: settings?.warranty_text || "All workmanship is warranted for 2 years from the date of completion. Paint product warranties are honored per the manufacturer.",
        terms: settings?.default_terms || "Payment due upon completion. Color changes after work begins may incur additional charges. Customer is responsible for moving valuables and small items.",
        expires_at: new Date(Date.now() + expDays * 86400_000).toISOString(),
        created_by: context.userId,
      })
      .select("*").single();
    if (error) throw new Error(error.message);

    if (items.length) {
      const rows = items.map((it) => ({
        proposal_id: prop.id,
        title: it.title || "Line item",
        description: it.description,
        area_or_quantity: it.area_or_quantity,
        unit: it.unit,
        unit_price: it.unit_price,
        total: it.total,
        sort_order: it.sort_order,
      }));
      await supabaseAdmin.from("proposal_line_items").insert(rows);
    }

    await supabaseAdmin.from("proposal_activity").insert({
      proposal_id: prop.id, activity_type: "created", actor_kind: "admin", actor_id: context.userId,
      note: "Created from voice dictation",
    });

    await syncProposalToCrm(supabaseAdmin, prop);

    return { id: prop.id, proposal_number: prop.proposal_number, public_token: prop.public_token };
  });

// -------- List proposals --------
export const listProposals = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: unknown) => z.object({
    status: z.string().optional(),
    search: z.string().optional(),
    limit: z.number().int().min(1).max(500).default(200),
  }).parse(d ?? {}))
  .handler(async ({ context, data }) => {
    await assertAdmin(context);
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    // Auto-expire any past-due proposals before listing.
    await supabaseAdmin
      .from("proposals")
      .update({ status: "expired" })
      .lt("expires_at", new Date().toISOString())
      .not("expires_at", "is", null)
      .not("status", "in", "(approved,declined,expired,void)");
    let q = supabaseAdmin.from("proposals").select("*").order("created_at", { ascending: false }).limit(data.limit);
    if (data.status) q = q.eq("status", data.status);
    if (data.search) {
      const s = `%${data.search.replace(/[%_]/g, "")}%`;
      q = q.or(`customer_name.ilike.${s},customer_email.ilike.${s},proposal_number.ilike.${s},property_address.ilike.${s}`);
    }
    const { data: rows, error } = await q;
    if (error) throw new Error(error.message);
    return rows ?? [];
  });

// -------- Get full proposal (admin) --------
export const getProposalForAdmin = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: unknown) => z.object({ id: z.string().uuid() }).parse(d))
  .handler(async ({ context, data }) => {
    await assertAdmin(context);
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const [p, items, colors, activity, changes] = await Promise.all([
      supabaseAdmin.from("proposals").select("*").eq("id", data.id).maybeSingle(),
      supabaseAdmin.from("proposal_line_items").select("*").eq("proposal_id", data.id).order("sort_order"),
      supabaseAdmin.from("proposal_colors").select("*").eq("proposal_id", data.id).order("sort_order"),
      supabaseAdmin.from("proposal_activity").select("*").eq("proposal_id", data.id).order("created_at", { ascending: false }).limit(100),
      supabaseAdmin.from("proposal_change_requests").select("*").eq("proposal_id", data.id).order("created_at", { ascending: false }),
    ]);
    if (p.error || !p.data) throw new Error(p.error?.message || "Not found");
    return {
      proposal: p.data,
      line_items: items.data ?? [],
      colors: colors.data ?? [],
      activity: activity.data ?? [],
      change_requests: changes.data ?? [],
    };
  });

// -------- Update proposal --------
export const updateProposal = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: unknown) => z.object({
    id: z.string().uuid(),
    patch: ProposalPatchSchema,
  }).parse(d))
  .handler(async ({ context, data }) => {
    await assertAdmin(context);
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { error } = await supabaseAdmin.from("proposals").update(data.patch).eq("id", data.id);
    if (error) throw new Error(error.message);
    await supabaseAdmin.from("proposal_activity").insert({
      proposal_id: data.id, activity_type: "edited", actor_kind: "admin", actor_id: context.userId,
      metadata: { keys: Object.keys(data.patch) },
    });
    return { ok: true };
  });

// -------- Replace line items / colors --------
export const replaceLineItems = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: unknown) => z.object({
    proposal_id: z.string().uuid(),
    items: z.array(LineItemSchema).max(100),
  }).parse(d))
  .handler(async ({ context, data }) => {
    await assertAdmin(context);
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    await supabaseAdmin.from("proposal_line_items").delete().eq("proposal_id", data.proposal_id);
    if (data.items.length) {
      const rows = data.items.map((i, idx) => ({
        proposal_id: data.proposal_id,
        title: i.title,
        description: i.description ?? null,
        area_or_quantity: i.area_or_quantity ?? null,
        unit: i.unit ?? null,
        unit_price: i.unit_price ?? null,
        total: i.total ?? 0,
        sort_order: idx,
      }));
      const { error } = await supabaseAdmin.from("proposal_line_items").insert(rows);
      if (error) throw new Error(error.message);
    }
    // recompute subtotal/total
    const subtotal = data.items.reduce((s, i) => s + (i.total || 0), 0);
    const { data: prop } = await supabaseAdmin.from("proposals").select("discount").eq("id", data.proposal_id).maybeSingle();
    const total = Math.max(0, subtotal - (prop?.discount ?? 0));
    await supabaseAdmin.from("proposals").update({ subtotal, total }).eq("id", data.proposal_id);
    return { ok: true, subtotal, total };
  });

export const replaceColors = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: unknown) => z.object({
    proposal_id: z.string().uuid(),
    colors: z.array(ColorSchema).max(50),
  }).parse(d))
  .handler(async ({ context, data }) => {
    await assertAdmin(context);
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    await supabaseAdmin.from("proposal_colors").delete().eq("proposal_id", data.proposal_id);
    if (data.colors.length) {
      const rows = data.colors.map((c, idx) => ({
        proposal_id: data.proposal_id,
        surface: c.surface,
        color_name: c.color_name ?? null,
        color_number: c.color_number ?? null,
        hex: c.hex ?? null,
        lrv: c.lrv ?? null,
        product_line: c.product_line ?? null,
        sheen: c.sheen ?? null,
        coats: c.coats,
        sort_order: idx,
      }));
      const { error } = await supabaseAdmin.from("proposal_colors").insert(rows);
      if (error) throw new Error(error.message);
    }
    return { ok: true };
  });

// -------- Send proposal (email + status) --------
export const sendProposal = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: unknown) => z.object({
    id: z.string().uuid(),
    origin: z.string().url().optional(),
  }).parse(d))
  .handler(async ({ context, data }) => {
    await assertAdmin(context);
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { data: prop, error } = await supabaseAdmin.from("proposals").select("*").eq("id", data.id).maybeSingle();
    if (error || !prop) throw new Error(error?.message || "Not found");
    if (!prop.customer_email) throw new Error("Customer has no email address.");

    const { data: settings } = await supabaseAdmin.from("admin_settings").select("*").eq("id", 1).maybeSingle();
    const origin = resolvePublicOrigin(settings);
    const link = `${origin}/proposal/${prop.public_token}`;
    const fmt = (n: number) => new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(n);
    const depositLine = prop.deposit_required
      ? `Deposit to schedule: ${fmt(Number(prop.deposit_amount))}`
      : "No deposit required.";

    // Send via built-in email queue using the proposal-ready template. Fail-soft.
    let emailResult: { ok: boolean; skipped?: boolean; error?: string } = { ok: false, skipped: true };
    try {
      const { sendAppEmail } = await import("@/lib/email/send-app.server");
      const res = await sendAppEmail({
        templateName: "proposal-ready",
        recipientEmail: prop.customer_email,
        // Include a timestamp so re-sends (edits, changes-requested follow-ups)
        // are not deduplicated with the original send.
        idempotencyKey: `proposal-ready-${prop.id}-${Date.now()}`,
        replyTo: settings?.business_email || undefined,
        templateData: {
          name: prop.customer_name,
          proposalNumber: prop.proposal_number,
          projectName: prop.project_type || null,
          totalAmount: Number(prop.total || 0),
          validUntil: prop.expires_at ? new Date(prop.expires_at).toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" }) : null,
          proposalUrl: link,
        },
      });
      if ("ok" in res && res.ok) emailResult = { ok: true };
      else emailResult = { ok: false, skipped: (res as any).skipped, error: (res as any).error || (res as any).reason };
    } catch (e) {
      emailResult = { ok: false, error: (e as Error).message };
    }

    await supabaseAdmin.from("proposals").update({
      // Reset to "sent" from any pre-decision state (draft / changes_requested / expired)
      // so re-sending after edits reflects the new state. Terminal statuses are preserved.
      status: ["approved", "declined", "void"].includes(prop.status)
        ? prop.status
        : "sent",
      sent_at: new Date().toISOString(),
    }).eq("id", prop.id);

    await supabaseAdmin.from("proposal_activity").insert({
      proposal_id: prop.id, activity_type: "sent", actor_kind: "admin", actor_id: context.userId,
      note: `Sent to ${prop.customer_email}`,
      metadata: { email: emailResult },
    });

    await syncProposalToCrm(supabaseAdmin, prop, { advanceToProposal: true });
    try {
      const { upsertOpportunityFromProposal } = await import("@/lib/crm/opportunities.server");
      await upsertOpportunityFromProposal(supabaseAdmin, prop as any, { markSent: true });
    } catch (e) { console.warn("[opportunity sync send]", e); }

    // Customer push if they've claimed the portal (fail-soft).
    const customerUserId = (prop as { customer_user_id?: string | null }).customer_user_id ?? null;
    if (customerUserId) {
      try {
        const { sendPushToUser } = await import("@/lib/push-sender.server");
        await sendPushToUser(customerUserId, "customer", "proposals", {
          title: "Your proposal is ready",
          body: `Proposal ${prop.proposal_number} · ${new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(Number(prop.total || 0))}`,
          url: `/portal/proposal`,
          tag: `proposal-ready-${prop.id}`,
        });
      } catch (e) { console.warn("[proposal customer push]", e); }
    }

    return { ok: true, link, email: emailResult };
  });

// -------- Void / mark won --------
export const setProposalStatus = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: unknown) => z.object({
    id: z.string().uuid(),
    status: StatusEnum,
    reason: z.string().max(2000).optional(),
  }).parse(d))
  .handler(async ({ context, data }) => {
    await assertAdmin(context);
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const requiresReason = data.status === "approved" || data.status === "declined";
    if (requiresReason && (!data.reason || data.reason.trim().length < 3)) {
      throw new Error("A reason is required when manually marking a proposal approved or declined.");
    }

    const patch: Record<string, any> = { status: data.status };
    if (data.status === "approved") {
      patch.approved_at = new Date().toISOString();
      patch.approved_by_name = "Manual admin override";
    } else if (data.status === "declined") {
      patch.declined_at = new Date().toISOString();
      patch.decline_reason = data.reason;
    }

    const { error } = await supabaseAdmin.from("proposals").update(patch as any).eq("id", data.id);
    if (error) throw new Error(error.message);

    const activityType =
      requiresReason ? "manual_override"
      : data.status === "void" ? "voided"
      : "edited";
    await supabaseAdmin.from("proposal_activity").insert({
      proposal_id: data.id,
      activity_type: activityType,
      actor_kind: "admin",
      actor_id: context.userId,
      actor_name: requiresReason ? "Manual admin override" : null,
      note: requiresReason ? `Manual admin override → ${data.status}` : `Status → ${data.status}`,
      metadata: {
        new_status: data.status,
        reason: data.reason ?? null,
        admin_user_id: context.userId,
        at: new Date().toISOString(),
      },
    });

    // Also write to the global audit log so super-admins can review overrides.
    if (requiresReason) {
      try {
        await supabaseAdmin.from("admin_audit_log").insert({
          action: "proposal.manual_override",
          target_type: "proposal",
          target_id: data.id,
          metadata: {
            new_status: data.status,
            reason: data.reason ?? null,
            admin_user_id: context.userId,
          },
        } as any);
      } catch (e) { console.warn("[audit override]", e); }
    }
    return { ok: true };
  });

// -------- Admin draft preview (server-side, requires admin auth) --------
export const getProposalAdminPreview = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: unknown) => z.object({ id: z.string().uuid() }).parse(d))
  .handler(async ({ context, data }) => {
    await assertAdmin(context);
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const [p, items, colors, settings] = await Promise.all([
      supabaseAdmin.from("proposals").select("*").eq("id", data.id).maybeSingle(),
      supabaseAdmin.from("proposal_line_items").select("*").eq("proposal_id", data.id).order("sort_order"),
      supabaseAdmin.from("proposal_colors").select("*").eq("proposal_id", data.id).order("sort_order"),
      supabaseAdmin.from("admin_settings").select("business_name, business_email, business_phone, company_website, company_logo_url").eq("id", 1).maybeSingle(),
    ]);
    if (p.error || !p.data) throw new Error(p.error?.message || "Not found");
    const s = settings.data;
    return {
      proposal: p.data,
      line_items: items.data ?? [],
      colors: colors.data ?? [],
      company: s ? {
        name: s.business_name ?? "Michael's Painting",
        email: s.business_email,
        phone: s.business_phone,
        website: s.company_website,
        logo_url: s.company_logo_url,
      } : {},
      admin_preview: true as const,
    };
  });

// -------- Extend expiration --------
export const extendProposalExpiration = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: unknown) => z.object({
    id: z.string().uuid(),
    days: z.number().int().min(1).max(180),
  }).parse(d))
  .handler(async ({ context, data }) => {
    await assertAdmin(context);
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { data: prop } = await supabaseAdmin.from("proposals").select("expires_at, status").eq("id", data.id).maybeSingle();
    const base = prop?.expires_at && new Date(prop.expires_at) > new Date()
      ? new Date(prop.expires_at)
      : new Date();
    base.setUTCDate(base.getUTCDate() + data.days);
    const newExp = base.toISOString();
    // If currently expired, revive to "sent" so customer can re-engage.
    const nextStatus = prop?.status === "expired" ? "sent" : prop?.status;
    await supabaseAdmin.from("proposals").update({ expires_at: newExp, status: nextStatus }).eq("id", data.id);
    await supabaseAdmin.from("proposal_activity").insert({
      proposal_id: data.id, activity_type: "expiration_extended", actor_kind: "admin", actor_id: context.userId,
      note: `Extended +${data.days} day(s)`,
      metadata: { new_expires_at: newExp, prior_status: prop?.status },
    });
    return { ok: true, expires_at: newExp };
  });

// -------- Resend proposal (re-email customer, log activity, no status downgrade) --------
export const resendProposal = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: unknown) => z.object({
    id: z.string().uuid(),
    origin: z.string().url().optional(),
  }).parse(d))
  .handler(async ({ context, data }) => {
    await assertAdmin(context);
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { data: prop } = await supabaseAdmin.from("proposals").select("*").eq("id", data.id).maybeSingle();
    if (!prop) throw new Error("Not found");
    if (!prop.customer_email) throw new Error("Customer has no email address.");

    const { data: settings } = await supabaseAdmin.from("admin_settings").select("*").eq("id", 1).maybeSingle();
    const origin = resolvePublicOrigin(settings);
    const link = `${origin}/proposal/${prop.public_token}`;
    const fmt = (n: number) => new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(n);
    const depositLine = prop.deposit_required
      ? `Deposit to schedule: ${fmt(Number(prop.deposit_amount))}`
      : "No deposit required.";

    void depositLine;

    // Send via the built-in Lovable email queue (same path as the original send),
    // using a unique idempotency key per resend so it isn't deduplicated.
    let emailResult: { ok: boolean; skipped?: boolean; error?: string } = { ok: false, skipped: true };
    try {
      const { sendAppEmail } = await import("@/lib/email/send-app.server");
      const res = await sendAppEmail({
        templateName: "proposal-ready",
        recipientEmail: prop.customer_email,
        idempotencyKey: `proposal-ready-${prop.id}-resend-${Date.now()}`,
        replyTo: settings?.business_email || undefined,
        templateData: {
          name: prop.customer_name,
          proposalNumber: prop.proposal_number,
          projectName: prop.project_type || null,
          totalAmount: Number(prop.total || 0),
          validUntil: prop.expires_at ? new Date(prop.expires_at).toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" }) : null,
          proposalUrl: link,
          isReminder: true,
        },
      });
      if ("ok" in res && res.ok) emailResult = { ok: true };
      else emailResult = { ok: false, skipped: (res as any).skipped, error: (res as any).error || (res as any).reason };
    } catch (e) {
      emailResult = { ok: false, error: (e as Error).message };
    }

    await supabaseAdmin.from("proposals").update({ sent_at: new Date().toISOString() }).eq("id", data.id);
    await supabaseAdmin.from("proposal_activity").insert({
      proposal_id: data.id, activity_type: "resent", actor_kind: "admin", actor_id: context.userId,
      note: `Resent to ${prop.customer_email}`,
      metadata: { email: emailResult },
    });
    return { ok: true, email: emailResult };
  });

// -------- Email diagnostics (status only — never returns secret values) --------
export const getProposalDiagnostics = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    await assertAdmin(context);
    return {
      RESEND_API_KEY: !!process.env.RESEND_API_KEY,
      RESEND_FROM: !!process.env.RESEND_FROM,
      RESEND_ADMIN_TO: !!process.env.RESEND_ADMIN_TO,
    };
  });

// -------- Server-rendered PDF (returns base64) --------
export const generateProposalPdf = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: unknown) => z.object({ id: z.string().uuid() }).parse(d))
  .handler(async ({ context, data }) => {
    await assertAdmin(context);
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const [p, items, colors, settings] = await Promise.all([
      supabaseAdmin.from("proposals").select("*").eq("id", data.id).maybeSingle(),
      supabaseAdmin.from("proposal_line_items").select("*").eq("proposal_id", data.id).order("sort_order"),
      supabaseAdmin.from("proposal_colors").select("*").eq("proposal_id", data.id).order("sort_order"),
      supabaseAdmin.from("admin_settings").select("business_name, business_email, business_phone, company_website, company_logo_url").eq("id", 1).maybeSingle(),
    ]);
    if (p.error || !p.data) throw new Error(p.error?.message || "Not found");
    const company = settings.data ? {
      name: settings.data.business_name ?? "Michael's Painting",
      email: settings.data.business_email,
      phone: settings.data.business_phone,
      website: settings.data.company_website,
      logo_url: settings.data.company_logo_url,
    } : {};
    const { renderProposalPdf } = await import("@/lib/proposals-pdf.server");
    const bytes = await renderProposalPdf({
      proposal: p.data,
      line_items: items.data ?? [],
      colors: colors.data ?? [],
      company,
    });
    // Base64 encode for safe RPC transport.
    let binary = "";
    const chunk = 0x8000;
    for (let i = 0; i < bytes.length; i += chunk) {
      binary += String.fromCharCode(...bytes.subarray(i, i + chunk));
    }
    const base64 = btoa(binary);
    return {
      filename: `Proposal-${p.data.proposal_number || p.data.id}.pdf`,
      mimeType: "application/pdf",
      base64,
      bytes: bytes.length,
    };
  });

// -------- PUBLIC: get proposal by token --------
export const getPublicProposal = createServerFn({ method: "POST" })
  .inputValidator((d: unknown) => z.object({ token: z.string().min(20).max(120) }).parse(d))
  .handler(async ({ data }) => {
    const { createClient } = await import("@supabase/supabase-js");
    const client = createClient(process.env.SUPABASE_URL!, process.env.SUPABASE_PUBLISHABLE_KEY!, {
      auth: { storage: undefined, persistSession: false, autoRefreshToken: false } as any,
    });
    const { data: result, error } = await client.rpc("get_proposal_by_token", { _token: data.token });
    if (error) throw new Error(error.message);
    if (!result) return null;
    // Pull a sanitized slice of company info server-side (admin_settings is admin-only via RLS).
    let company: { name?: string; phone?: string | null; email?: string | null; website?: string | null; logo_url?: string | null } = {};
    try {
      const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
      const { data: s } = await supabaseAdmin
        .from("admin_settings")
        .select("business_name, business_email, business_phone, company_website, company_logo_url")
        .eq("id", 1)
        .maybeSingle();
      if (s) {
        company = {
          name: s.business_name ?? "Michael's Painting",
          email: s.business_email,
          phone: s.business_phone,
          website: s.company_website,
          logo_url: s.company_logo_url,
        };
      }
    } catch {
      // fail soft — public page renders sensible defaults
    }
    return { ...(result as Record<string, unknown>), company } as {
      proposal: any;
      line_items: any[];
      colors: any[];
      company: typeof company;
    };
  });

// -------- PUBLIC: server-rendered PDF (by token) --------
export const generatePublicProposalPdf = createServerFn({ method: "POST" })
  .inputValidator((d: unknown) => z.object({ token: z.string().min(20).max(120) }).parse(d))
  .handler(async ({ data }) => {
    const { createClient } = await import("@supabase/supabase-js");
    const client = createClient(process.env.SUPABASE_URL!, process.env.SUPABASE_PUBLISHABLE_KEY!, {
      auth: { storage: undefined, persistSession: false, autoRefreshToken: false } as any,
    });
    const { data: result, error } = await client.rpc("get_proposal_by_token", { _token: data.token });
    if (error) throw new Error(error.message);
    if (!result) throw new Error("Proposal not found");
    const r = result as any;
    let company: any = {};
    try {
      const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
      const { data: s } = await supabaseAdmin
        .from("admin_settings")
        .select("business_name, business_email, business_phone, company_website, company_logo_url")
        .eq("id", 1)
        .maybeSingle();
      if (s) company = {
        name: s.business_name ?? "Michael's Painting",
        email: s.business_email,
        phone: s.business_phone,
        website: s.company_website,
        logo_url: s.company_logo_url,
      };
    } catch { /* fail soft */ }
    const { renderProposalPdf } = await import("@/lib/proposals-pdf.server");
    const bytes = await renderProposalPdf({
      proposal: r.proposal,
      line_items: r.line_items ?? [],
      colors: r.colors ?? [],
      company,
    });
    let binary = "";
    const chunk = 0x8000;
    for (let i = 0; i < bytes.length; i += chunk) {
      binary += String.fromCharCode(...bytes.subarray(i, i + chunk));
    }
    return {
      filename: `Proposal-${r.proposal?.proposal_number || "proposal"}.pdf`,
      mimeType: "application/pdf",
      base64: btoa(binary),
      bytes: bytes.length,
    };
  });

export const recordPublicView = createServerFn({ method: "POST" })
  .inputValidator((d: unknown) => z.object({ token: z.string().min(20).max(120) }).parse(d))
  .handler(async ({ data }) => {
    let firstView = false;
    try {
      const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
      const { data: row } = await supabaseAdmin
        .from("proposals")
        .select("first_viewed_at")
        .eq("public_token", data.token)
        .maybeSingle();
      firstView = !!row && row.first_viewed_at == null;
    } catch { /* fail soft */ }

    const { createClient } = await import("@supabase/supabase-js");
    const client = createClient(process.env.SUPABASE_URL!, process.env.SUPABASE_PUBLISHABLE_KEY!, {
      auth: { storage: undefined, persistSession: false, autoRefreshToken: false } as any,
    });
    const { getRequestHeader } = await import("@tanstack/react-start/server");
    const ua = (getRequestHeader("user-agent") || "").slice(0, 400);
    await client.rpc("record_proposal_view", { _token: data.token, _user_agent: ua });

    if (firstView) {
      try {
        const { notifyAdminProposalEvent } = await import("@/lib/proposals.server");
        await notifyAdminProposalEvent({ token: data.token, event: "viewed" });
      } catch (e) { console.warn("[proposal notify]", e); }
    }
    return { ok: true };
  });

export const submitPublicDecision = createServerFn({ method: "POST" })
  .inputValidator((d: unknown) => z.object({
    token: z.string().min(20).max(120),
    decision: z.enum(["approve","decline"]),
    name: z.string().max(200).optional(),
    reason: z.string().max(2000).optional(),
    accepted_terms: z.boolean().optional(),
    signature: z.string().max(500000).optional(),
  }).parse(d))
  .handler(async ({ data }) => {
    if (data.decision === "approve" && !data.accepted_terms) {
      throw new Error("You must accept the terms to approve.");
    }
    const { getRequestHeader, getRequestIP } = await import("@tanstack/react-start/server");
    const { hashIp } = await import("@/lib/proposals.server");
    const ip = getRequestIP({ xForwardedFor: true }) || "";
    const ua = (getRequestHeader("user-agent") || "").slice(0, 400);
    const { createClient } = await import("@supabase/supabase-js");
    const client = createClient(process.env.SUPABASE_URL!, process.env.SUPABASE_PUBLISHABLE_KEY!, {
      auth: { storage: undefined, persistSession: false, autoRefreshToken: false } as any,
    });
    const { data: out, error } = await client.rpc("submit_proposal_decision", {
      _token: data.token,
      _decision: data.decision,
      _name: data.name || "",
      _reason: data.reason || "",
      _ip_hash: hashIp(ip),
      _user_agent: ua,
      _signature: data.signature || null,
    });
    if (error) throw new Error(error.message);
    // Notify admin (fail-soft).
    try {
      const { notifyAdminProposalEvent } = await import("@/lib/proposals.server");
      await notifyAdminProposalEvent({
        token: data.token,
        event: data.decision === "approve" ? "approved" : "declined",
        actorName: data.name,
        note: data.reason,
      });
    } catch (e) { console.warn("[proposal notify]", e); }
    // Auto-create draft deposit invoice on approval (fail-soft).
    if (data.decision === "approve") {
      try {
        const proposalId = (out as any)?.proposal_id ?? (out as any)?.id;
        if (proposalId) {
          const { createDepositInvoiceFromProposal } = await import("@/lib/accounting/accounting.functions");
          await createDepositInvoiceFromProposal(proposalId);
        }
      } catch (e) { console.warn("[deposit invoice automation]", e); }
    }
    // Move the linked opportunity to Won or Lost (fail-soft).
    try {
      const proposalId = (out as any)?.proposal_id ?? (out as any)?.id;
      if (proposalId) {
        const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
        const { markOpportunityFromProposalDecision } = await import("@/lib/crm/opportunities.server");
        await markOpportunityFromProposalDecision(supabaseAdmin, proposalId, data.decision as any, data.reason);
      }
    } catch (e) { console.warn("[opportunity decision sync]", e); }
    return out;
  });

export const submitPublicChangeRequest = createServerFn({ method: "POST" })
  .inputValidator((d: unknown) => z.object({
    token: z.string().min(20).max(120),
    name: z.string().max(200).optional(),
    email: z.string().email().max(200).optional(),
    message: z.string().min(1).max(4000),
  }).parse(d))
  .handler(async ({ data }) => {
    const { createClient } = await import("@supabase/supabase-js");
    const client = createClient(process.env.SUPABASE_URL!, process.env.SUPABASE_PUBLISHABLE_KEY!, {
      auth: { storage: undefined, persistSession: false, autoRefreshToken: false } as any,
    });
    const { error } = await client.rpc("submit_proposal_change_request", {
      _token: data.token,
      _name: data.name || "",
      _email: data.email || "",
      _message: data.message,
    });
    if (error) throw new Error(error.message);
    try {
      const { notifyAdminProposalEvent } = await import("@/lib/proposals.server");
      await notifyAdminProposalEvent({
        token: data.token,
        event: "changes_requested",
        actorName: data.name,
        note: data.message,
      });
    } catch (e) { console.warn("[proposal notify]", e); }
    return { ok: true };
  });

// Wrap recordPublicView too to notify on first view.